import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Youtube, Instagram, Twitter, MessageCircle, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL, NAV_LINKS, WHATSAPP_NUMBER } from "@/lib/constants";
import api, { formatApiError } from "@/lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Inscription confirmée.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-brand-blue text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <img src={LOGO_URL} alt="DEFAR" className="h-14 w-auto bg-white p-2 rounded-sm" />
            <p className="mt-5 text-sm text-blue-100 leading-relaxed max-w-xs">
              DEFAR - Les Bâtisseurs. Un mouvement citoyen au service du Sénégal,
              engagé pour bâtir un avenir de progrès, de justice et de solidarité.
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { Icon: Facebook, href: "https://www.facebook.com/sndefar" },
                { Icon: Youtube, href: "https://youtube.com" },
                { Icon: Instagram, href: "https://instagram.com" },
                { Icon: Twitter, href: "https://x.com" },
                { Icon: MessageCircle, href: `https://wa.me/${WHATSAPP_NUMBER}` },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 flex items-center justify-center border border-blue-400/40 hover:bg-brand-green hover:border-brand-green transition-colors"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-sm font-bold uppercase tracking-wider text-blue-200 mb-4">Plan du site</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-blue-100 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-bold uppercase tracking-wider text-blue-200 mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-blue-100">
              <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0" /> Avenue Cheikh Anta Diop, Dakar, Sénégal</li>
              <li className="flex items-center gap-2"><Phone size={16} /> +221 77 000 00 00</li>
              <li className="flex items-center gap-2"><Mail size={16} /> contact@defar.sn</li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-bold uppercase tracking-wider text-blue-200 mb-4">Newsletter</h4>
            <p className="text-sm text-blue-100 mb-4">Recevez les actualités du mouvement.</p>
            <form onSubmit={subscribe} className="flex flex-col gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-input"
                placeholder="Votre email"
                className="bg-white/10 border border-blue-400/40 px-4 py-2.5 text-sm placeholder:text-blue-200 focus:outline-none focus:border-white"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="bg-brand-green hover:bg-brand-greenhover transition-colors px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
              >
                {loading ? "..." : "S'inscrire"}
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-blue-400/30 mt-12 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-blue-200">
          <p>© {new Date().getFullYear()} DEFAR - Les Bâtisseurs. Tous droits réservés.</p>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
            <Link to="/contact" className="hover:text-white">Contact presse</Link>
            <Link to="/admin/login" className="hover:text-white">Espace admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
